import type { ServiceResult } from "@core/services/serviceResult";

import { AreaType, LotType } from "@common/enums";
import httpClient from "@common/http-client";
import { getEnumValue } from "@common/utils/enum";
import { mapServiceErrorKeyWithPrefixes } from "@common/utils/http/errors";
import { normalizeObjectId } from "@common/utils/objects";
import { normalizeNullableString, normalizeString } from "@common/utils/strings";

import type {
  ParkingVisitsResponse,
  TollVisitsResponse,
  VideoMetadata,
  Visit,
  VisitBasis,
  YouparkArea,
  YouparkConfiguration,
  YouparkConfigurationSummary,
  YouparkLot
} from "@/types/youpark";

type RawArea = {
  id?: string;
  name?: string;
  type?: number;
  typeStr?: string;
  lotIds?: unknown;
  latitude?: number;
  longitude?: number;
};

type RawLot = {
  id?: string;
  name?: string;
  lotType?: number;
  capacity?: number;
  enableInventory?: boolean;
};

type RawConfigurationResponse = {
  id?: string;
  organizationId?: string;
  organizationName?: string;
  lots?: RawLot[];
  areas?: RawArea[];
  allowBusinessInsights?: boolean;
  users?: unknown;
  selectableRemitCreditors?: unknown;
  collectionProviderSettings?: unknown;
  collectionProviderVendors?: unknown;
  commissionAgreements?: unknown;
  remitAllocationSchemas?: unknown;
  youpayConfigurations?: unknown;
  accessApplications?: unknown;
  freeVisitKioskConfigurations?: unknown;
  fees?: unknown;
  hasAccessProduct?: boolean;
};

type RawVisitBasis = {
  id?: string;
  type?: number;
  description?: string;
  amount?: number;
  amountStr?: string;
  periodStart?: string;
  periodEnd?: string;
  durations?: { start?: string; end?: string }[];
  subscriptionId?: string;
  templateId?: string;
  templateName?: string;
  subscriptionVoucherIsValueCard?: boolean;
};

type RawVisit = {
  alprId?: string;
  lotId?: string;
  lotName?: string;
  licenseplate?: string;
  recognitionDate?: string;
  totalEarnings?: number;
  totalEarningsMoney?: string;
  hasSubscription?: boolean;
  directionType?: number;
  subscriptionVoucherTotalCountSpent?: number;
  lossType?: number;
  classificationVisible?: boolean;
  classificationLabel?: string;
  classificationIsVehicle?: boolean;
  classificationCount?: number;
  classificationConfidence?: number;
  vehicleMakeModel?: string | null;
  vehicleMakeModelScore?: number | null;
  vehicleColor?: string | null;
  vehicleColorScore?: number | null;
  basises?: RawVisitBasis[];
  countryType?: number;
  countryTypeStr?: string;
  vehicleInfoId?: string | null;
  vehicleInfoTotalWeightInKilos?: number | null;
  vehicleInfoTonnagePriceCategoryType?: number;
  vehicleInfoTonnagePriceCategoryTypeStr?: string;
  barrierOpenDate?: string | null;
  isEmployee?: boolean;
  manuallyApproved?: boolean;
  manuallyApprovedByName?: string;
  isAtParkingLotUntilDate?: string | null;
  isAtParkingLotUntilDatePolicyTypeStr?: string | null;
  isInventoryEnabledForLot?: boolean;
  linkedVisitId?: string | null;
  linkedVisitLotName?: string | null;
  linkedVisitLicensePlate?: string | null;
  linkedVisitCountryTypeStr?: string | null;
  isRemovable?: boolean;
  test?: boolean;
  subscriptionVoucherIsValueCard?: boolean;
};

type RawLotVehiclesResponse = {
  totalBillableEarningsToday?: number;
  totalBillableEarningsTodayMoney?: string;
  totalBillableVisitsTodayCount?: number;
  totalVehiclesNowCount?: number;
  totalVisitsTodayCount?: number;
  totalVisitsTodayEnterCount?: number;
  totalVisitsTodayExitCount?: number;
  totalSubscriptionVouchersCount?: number;
  vehicles?: RawVisit[];
  now?: string;
  totalVehiclesSearchCount?: number;
  isInventoryEnabled?: boolean;
};

const CONFIG_ERROR = "youpark.errors.loadConfig";
const VISITS_ERROR = "youpark.errors.loadVisits";
const VIDEO_ERROR = "youpark.errors.loadVideo";
const REMOVE_ERROR = "youpark.errors.removeEntry";
const EMPTY_LOTS_ERROR = "youpark.errors.emptyLots";

const errorMap: Record<string, string> = {
  ERR_ACCESS_DENIED: "youpark.errors.accessDenied",
  ERR_RATE_LIMIT: "youpark.errors.rateLimit",
  ERR_LOTS_EMPTY: "youpark.errors.noLotsSelected",
  ERR_LOT_COUNT_MISMATCH: "youpark.errors.lotCountMismatch",
  ERR_LOT_INCORRECT_TYPE: "youpark.errors.invalidLotType"
};

const mapError = (error: unknown, fallback: string) =>
  mapServiceErrorKeyWithPrefixes(error, fallback, errorMap, ["youpark"]);

type CacheEntry<T> = {
  fetchedAt: number;
  data: T;
};

const CONFIGURATION_CACHE_TTL_MS = 30000;
const CONFIGURATION_DEFAULT_TIMEOUT_MS = 30000;

const configurationCacheByOrganizationId = new Map<string, CacheEntry<YouparkConfiguration>>();
const pendingConfigurationByOrganizationId = new Map<string, Promise<YouparkConfiguration>>();

const toArea = (raw: RawArea): YouparkArea => ({
  id: normalizeObjectId(raw.id ?? "", ""),
  name: raw.name?.toString().trim() ?? "",
  type: getEnumValue(raw.type, AreaType),
  typeLabel: raw.typeStr?.toString().trim() ?? "",
  lotIds: Array.isArray(raw.lotIds)
    ? raw.lotIds
        .map(id => normalizeObjectId(typeof id === "string" ? id : String(id ?? ""), ""))
        .filter(id => id !== "")
    : [],
  latitude: typeof raw.latitude === "number" ? raw.latitude : undefined,
  longitude: typeof raw.longitude === "number" ? raw.longitude : undefined
});

const toLot = (raw: RawLot): YouparkLot => ({
  id: normalizeObjectId(raw.id ?? "", ""),
  name: raw.name?.toString().trim() ?? "",
  lotType: getEnumValue(raw.lotType, LotType),
  capacity: typeof raw.capacity === "number" ? raw.capacity : 0,
  enableInventory: raw.enableInventory === true
});

const toBasis = (raw: RawVisitBasis): VisitBasis => ({
  id: normalizeObjectId(raw.id ?? ""),
  type: typeof raw.type === "number" ? raw.type : 0,
  description: normalizeNullableString(raw.description) ?? undefined,
  amount: typeof raw.amount === "number" ? raw.amount : undefined,
  amountStr: normalizeNullableString(raw.amountStr) ?? undefined,
  periodStart: normalizeNullableString(raw.periodStart) ?? undefined,
  periodEnd: normalizeNullableString(raw.periodEnd) ?? undefined,
  durations:
    raw.durations?.map(duration => ({
      start: normalizeNullableString(duration?.start) ?? "",
      end: normalizeNullableString(duration?.end) ?? ""
    })) ?? [],
  subscriptionId: normalizeNullableString(raw.subscriptionId) ?? undefined,
  templateId: normalizeNullableString(raw.templateId) ?? undefined,
  templateName: normalizeNullableString(raw.templateName) ?? undefined,
  subscriptionVoucherIsValueCard: raw.subscriptionVoucherIsValueCard === true
});

const toVisit = (raw: RawVisit, lotType: LotType): Visit => ({
  alprId: normalizeObjectId(raw.alprId ?? ""),
  lotId: normalizeObjectId(raw.lotId ?? ""),
  lotName: raw.lotName?.toString().trim() ?? "",
  lotType,
  licensePlate: normalizeNullableString(raw.licenseplate) ?? "",
  recognitionDate: normalizeNullableString(raw.recognitionDate) ?? "",
  totalEarnings: typeof raw.totalEarnings === "number" ? raw.totalEarnings : undefined,
  totalEarningsMoney: normalizeNullableString(raw.totalEarningsMoney) ?? undefined,
  hasSubscription: raw.hasSubscription === true,
  directionType: typeof raw.directionType === "number" ? raw.directionType : undefined,
  subscriptionVoucherTotalCountSpent:
    typeof raw.subscriptionVoucherTotalCountSpent === "number" ? raw.subscriptionVoucherTotalCountSpent : undefined,
  lossType: typeof raw.lossType === "number" ? raw.lossType : undefined,
  classificationVisible: raw.classificationVisible === true,
  classificationLabel: normalizeNullableString(raw.classificationLabel) ?? undefined,
  classificationIsVehicle: raw.classificationIsVehicle === true,
  classificationCount: typeof raw.classificationCount === "number" ? raw.classificationCount : undefined,
  classificationConfidence: typeof raw.classificationConfidence === "number" ? raw.classificationConfidence : undefined,
  vehicleMakeModel: normalizeNullableString(raw.vehicleMakeModel) ?? undefined,
  vehicleMakeModelScore:
    typeof raw.vehicleMakeModelScore === "number" && Number.isFinite(raw.vehicleMakeModelScore)
      ? raw.vehicleMakeModelScore
      : undefined,
  vehicleColor: normalizeNullableString(raw.vehicleColor) ?? undefined,
  vehicleColorScore:
    typeof raw.vehicleColorScore === "number" && Number.isFinite(raw.vehicleColorScore)
      ? raw.vehicleColorScore
      : undefined,
  basises: (raw.basises ?? []).map(toBasis),
  countryType: typeof raw.countryType === "number" ? raw.countryType : undefined,
  countryTypeStr: normalizeNullableString(raw.countryTypeStr) ?? undefined,
  vehicleInfoId: raw.vehicleInfoId ? normalizeObjectId(raw.vehicleInfoId) : null,
  vehicleInfoTotalWeightInKilos:
    typeof raw.vehicleInfoTotalWeightInKilos === "number" ? raw.vehicleInfoTotalWeightInKilos : null,
  vehicleInfoTonnagePriceCategoryType:
    typeof raw.vehicleInfoTonnagePriceCategoryType === "number" ? raw.vehicleInfoTonnagePriceCategoryType : undefined,
  vehicleInfoTonnagePriceCategoryTypeStr:
    normalizeNullableString(raw.vehicleInfoTonnagePriceCategoryTypeStr) ?? undefined,
  barrierOpenDate: normalizeNullableString(raw.barrierOpenDate) ?? null,
  isEmployee: raw.isEmployee === true,
  manuallyApproved: raw.manuallyApproved === true,
  manuallyApprovedByName: normalizeNullableString(raw.manuallyApprovedByName) ?? undefined,
  isAtParkingLotUntilDate: normalizeNullableString(raw.isAtParkingLotUntilDate) ?? null,
  isAtParkingLotUntilDatePolicyTypeStr: normalizeNullableString(raw.isAtParkingLotUntilDatePolicyTypeStr) ?? undefined,
  isInventoryEnabledForLot: raw.isInventoryEnabledForLot === true,
  linkedVisitId: normalizeNullableString(raw.linkedVisitId) ?? null,
  linkedVisitLotName: normalizeNullableString(raw.linkedVisitLotName) ?? undefined,
  linkedVisitLicensePlate: normalizeNullableString(raw.linkedVisitLicensePlate) ?? undefined,
  linkedVisitCountryTypeStr: normalizeNullableString(raw.linkedVisitCountryTypeStr) ?? undefined,
  isRemovable: raw.isRemovable !== false,
  test: raw.test === true,
  subscriptionVoucherIsValueCard: raw.subscriptionVoucherIsValueCard === true
});

const fetchConfiguration = async (
  organizationId: string,
  options?: { force?: boolean; timeoutMs?: number }
): Promise<YouparkConfiguration> => {
  const normalizedOrganizationId = organizationId.trim();
  if (!normalizedOrganizationId) {
    throw new Error(CONFIG_ERROR);
  }

  if (!options?.force) {
    const cached = configurationCacheByOrganizationId.get(normalizedOrganizationId);
    if (cached && Date.now() - cached.fetchedAt < CONFIGURATION_CACHE_TTL_MS) {
      return cached.data;
    }

    const pending = pendingConfigurationByOrganizationId.get(normalizedOrganizationId);
    if (pending) {
      return pending;
    }
  }

  const requestPromise = (async () => {
    const timeoutMs = Math.max(
      0,
      typeof options?.timeoutMs === "number" ? options.timeoutMs : CONFIGURATION_DEFAULT_TIMEOUT_MS
    );
    const controller = timeoutMs > 0 ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    try {
      const response = await httpClient.get<RawConfigurationResponse>(
        "/Youpark/GetConfiguration",
        {
          organizationId: normalizedOrganizationId
        },
        undefined,
        controller?.signal ?? undefined
      );

      if (!response.ok) {
        throw new Error(mapError(response.errorThrown, CONFIG_ERROR));
      }

      const data = (response.data ?? {}) as YouparkConfiguration;
      configurationCacheByOrganizationId.set(normalizedOrganizationId, { data, fetchedAt: Date.now() });
      return data;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      pendingConfigurationByOrganizationId.delete(normalizedOrganizationId);
    }
  })();

  pendingConfigurationByOrganizationId.set(normalizedOrganizationId, requestPromise);
  return requestPromise;
};

export const youparkService = {
  invalidateConfiguration(organizationId: string) {
    const normalized = organizationId.trim();
    configurationCacheByOrganizationId.delete(normalized);
    pendingConfigurationByOrganizationId.delete(normalized);
  },

  fetchConfiguration,

  async fetchConfigurationSummary(organizationId: string): Promise<ServiceResult<YouparkConfigurationSummary>> {
    try {
      const configuration = await fetchConfiguration(organizationId);

      const organizationName = normalizeNullableString(configuration.organizationName) ?? "";
      const lots = (configuration.lots ?? []).map(toLot).filter(lot => lot.id.length > 0 && lot.name.length > 0);
      const areas = (configuration.areas ?? []).map(toArea).filter(area => area.id.length > 0 && area.name.length > 0);
      const hasOnlyParkingLots = lots.length > 0 && lots.every(lot => lot.lotType === LotType.ParkingLot);
      const allowBusinessInsights =
        typeof configuration.allowBusinessInsights === "boolean"
          ? configuration.allowBusinessInsights
          : hasOnlyParkingLots;

      return {
        success: true,
        data: {
          organizationName,
          lots,
          areas,
          hasOnlyParkingLots,
          allowBusinessInsights
        }
      };
    } catch (error) {
      return { success: false, errorKey: mapError(error, CONFIG_ERROR) };
    }
  },

  async fetchParkingVisits(
    organizationId: string,
    page: number,
    lotIds: string[],
    query?: string | null,
    now?: string
  ): Promise<ServiceResult<ParkingVisitsResponse>> {
    const normalizedQueryValue = normalizeString(query);
    const normalizedQuery = normalizedQueryValue.length >= 2 ? normalizedQueryValue : undefined;
    const pageIndex = Math.max(0, page - 1);

    try {
      const response = await httpClient.get<RawLotVehiclesResponse>("/Youpark/GetVehiclesParkingLotsSummary", {
        organizationId,
        now,
        page: pageIndex,
        query: normalizedQuery,
        lotIds: lotIds.join(",")
      });

      if (!response.ok) {
        return { success: false, errorKey: mapError(response.errorThrown, VISITS_ERROR) };
      }

      const data = response.data ?? {};
      const visits = (data.vehicles ?? []).map(vehicle => toVisit(vehicle, LotType.ParkingLot));

      return {
        success: true,
        data: {
          totalBillableEarningsToday: data.totalBillableEarningsToday ?? 0,
          totalBillableEarningsTodayMoney: data.totalBillableEarningsTodayMoney ?? "",
          totalBillableVisitsTodayCount: data.totalBillableVisitsTodayCount ?? 0,
          totalVisitsTodayCount: data.totalVisitsTodayCount ?? 0,
          totalVehiclesNowCount: data.totalVehiclesNowCount ?? 0,
          totalVehiclesSearchCount: data.totalVehiclesSearchCount ?? 0,
          totalSubscriptionVouchersCount: data.totalSubscriptionVouchersCount ?? 0,
          now: data.now ?? "",
          isInventoryEnabled: data.isInventoryEnabled ?? false,
          visits
        }
      };
    } catch (error) {
      return { success: false, errorKey: mapError(error, VISITS_ERROR) };
    }
  },

  async fetchTollVisits(
    organizationId: string,
    page: number,
    lotIds: string[]
  ): Promise<ServiceResult<TollVisitsResponse>> {
    const pageIndex = Math.max(0, page - 1);
    try {
      const response = await httpClient.get<RawLotVehiclesResponse>("/Youpark/GetVehiclesTollRoadSummary", {
        organizationId,
        page: pageIndex,
        lotIds: lotIds.join(",")
      });

      if (!response.ok) {
        return { success: false, errorKey: mapError(response.errorThrown, VISITS_ERROR) };
      }

      const data = response.data ?? {};
      const visits = (data.vehicles ?? []).map(vehicle => toVisit(vehicle, LotType.TollRoad));

      return {
        success: true,
        data: {
          totalBillableEarningsToday: data.totalBillableEarningsToday ?? 0,
          totalBillableEarningsTodayMoney: data.totalBillableEarningsTodayMoney ?? "",
          totalBillableVisitsTodayCount: data.totalBillableVisitsTodayCount ?? 0,
          totalVisitsTodayCount: data.totalVisitsTodayCount ?? 0,
          totalVisitsTodayEnterCount: data.totalVisitsTodayEnterCount ?? 0,
          totalVisitsTodayExitCount: data.totalVisitsTodayExitCount ?? 0,
          totalSubscriptionVouchersCount: data.totalSubscriptionVouchersCount ?? 0,
          visits
        }
      };
    } catch (error) {
      return { success: false, errorKey: mapError(error, VISITS_ERROR) };
    }
  },

  async fetchVideoMetadata(
    lotId: string,
    lotType: LotType,
    alprResultId: string
  ): Promise<ServiceResult<VideoMetadata>> {
    try {
      const response = await httpClient.get<VideoMetadata>("/Youpark/GetLotRecognitionImageUrl", {
        lotId,
        lotType,
        alprResultId
      });

      if (!response.ok) {
        return { success: false, errorKey: mapError(response.errorThrown, VIDEO_ERROR) };
      }

      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, errorKey: mapError(error, VIDEO_ERROR) };
    }
  },

  async removeLotEnterResult(lotId: string, alprResultId: string): Promise<ServiceResult> {
    try {
      const response = await httpClient.delete(`/youpark/removelotenterresult/${lotId}/${alprResultId}`);
      if (!response.ok) {
        return { success: false, errorKey: mapError(response.errorThrown, REMOVE_ERROR) };
      }
      return { success: true };
    } catch (error) {
      return { success: false, errorKey: mapError(error, REMOVE_ERROR) };
    }
  },

  async emptyParkingLots(lotIds: string[]): Promise<ServiceResult> {
    try {
      const response = await httpClient.put("/youpark/emptyparkinglots", { lotIds });
      if (!response.ok) {
        return { success: false, errorKey: mapError(response.errorThrown, EMPTY_LOTS_ERROR) };
      }
      return { success: true };
    } catch (error) {
      return { success: false, errorKey: mapError(error, EMPTY_LOTS_ERROR) };
    }
  }
};
